import React, { useState, useEffect } from 'react';
import { ChevronUp, ChevronDown, X, Play, ExternalLink, ListMusic, User } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const spotifyEmbed = (uri) => {
  if (!uri) return '';
  // Remove existing query params
  let u = uri.split('?')[0];
  if (u.includes('open.spotify.com') && !u.includes('/embed/')) {
    u = u.replace('open.spotify.com/', 'open.spotify.com/embed/').replace('/intl-es/', '/');
  }
  return `${u}?utm_source=generator&theme=0`;
};

const OG_SPOTIFY = 'https://open.spotify.com/user/31gofgummrn24fopehxrjzonqxoq';

// Hardcoded defaults so artists show INSTANTLY (no API wait)
const DEFAULT_ARTISTS = [
  { name: 'ISKARA', spotify_url: 'https://open.spotify.com/artist/16AVQwtbLBtxeVfRIgLtV0' },
  { name: 'SOLEYA', spotify_url: 'https://open.spotify.com/artist/58qU8zjPPbYQAQutsUUEUx' },
  { name: 'PILDORA', spotify_url: 'https://open.spotify.com/artist/5QSDfhpRGUlJQxwD631UYW' },
  { name: 'NOYA', spotify_url: 'https://open.spotify.com/artist/64QJKPzCS6DbyxoTHoY7DW' },
  { name: 'KALEN ROSE', spotify_url: 'https://open.spotify.com/artist/6xmfcWx7RCQWidLcZNxjTt' },
  { name: 'ADONAY LUZ', spotify_url: 'https://open.spotify.com/artist/1epbLquvS0PCNb8Tq1bCEj' },
];

const useSpotifyData = () => {
  const [artists, setArtists] = useState(DEFAULT_ARTISTS);
  const [playlists, setPlaylists] = useState([]);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/artists`).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/api/spotify-playlists`).catch(() => ({ data: [] })),
    ]).then(([aRes, pRes]) => {
      const withSpotify = (aRes.data || []).filter(a => a.spotify_url);
      if (withSpotify.length > 0) setArtists(withSpotify);
      setPlaylists(pRes.data || []);
    });
  }, []);
  return { artists, playlists };
};

/* ─── Home Section ─── */
export const SpotifySection = () => {
  const { artists, playlists } = useSpotifyData();
  const [tab, setTab] = useState('artists');
  const [activeIdx, setActiveIdx] = useState(0);

  const items = tab === 'playlists' ? playlists : artists;
  const current = items[activeIdx] || items[0];
  const embedUrl = spotifyEmbed(current?.spotify_url || '');

  useEffect(() => { setActiveIdx(0); }, [tab]);

  return (
    <section className="section-padding bg-gradient-to-b from-transparent via-[#0a0a0c] to-transparent" data-testid="spotify-section">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-10">
          <p className="text-[#1DB954] text-xs font-bold uppercase tracking-[3px] mb-3">Escucha en Spotify</p>
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">Musica OG en <span className="text-[#1DB954]">Vivo</span></h2>
          <p className="text-white/50 text-sm max-w-xl mx-auto">Escucha directamente desde Spotify sin salir de la pagina.</p>
        </div>

        <div className="grid md:grid-cols-[280px_1fr] gap-6">
          <div className="space-y-2">
            <div className="flex gap-1 p-1 bg-white/[0.04] rounded-xl mb-4" data-testid="spotify-tab-toggle">
              <button onClick={() => setTab('artists')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all ${tab === 'artists' ? 'bg-[#1DB954] text-black' : 'text-white/50'}`}
                data-testid="spotify-tab-artists"><User className="w-3.5 h-3.5" />Artistas</button>
              <button onClick={() => setTab('playlists')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all ${tab === 'playlists' ? 'bg-[#1DB954] text-black' : 'text-white/50'}`}
                data-testid="spotify-tab-playlists"><ListMusic className="w-3.5 h-3.5" />Playlists</button>
            </div>

            <p className="text-[10px] text-white/40 uppercase tracking-wider mb-2 font-semibold">
              {tab === 'artists' ? 'Artistas OG MUSIC' : 'Playlists OG MUSIC'}
            </p>

            {items.length === 0 ? (
              <div className="text-center py-6 text-white/30 text-xs">{tab === 'playlists' ? 'Pronto agregaremos playlists' : 'Cargando...'}</div>
            ) : items.map((a, i) => (
              <button key={a.id || i} onClick={() => setActiveIdx(i)}
                className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all flex items-center gap-3 ${
                  i === activeIdx ? 'bg-[#1DB954]/15 text-[#1DB954] border border-[#1DB954]/30' : 'bg-white/[0.03] text-white/60 border border-white/[0.06] hover:bg-white/[0.06]'
                }`} data-testid={`spotify-item-${i}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${i === activeIdx ? 'bg-[#1DB954] text-black' : 'bg-white/10 text-white/40'}`}>
                  {tab === 'playlists' ? <ListMusic className="w-3.5 h-3.5" /> : a.name?.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="truncate">{a.name}</div>
                  {a.description && <div className="text-[9px] text-white/30 truncate">{a.description}</div>}
                </div>
                {i === activeIdx && <Play className="w-3 h-3 shrink-0" />}
              </button>
            ))}

            <a href={OG_SPOTIFY} target="_blank" rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 mt-4 rounded-xl text-sm font-semibold bg-[#1DB954] hover:bg-[#1ed760] text-black transition-all"
              data-testid="spotify-profile-link">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
              Ver Perfil Completo <ExternalLink className="w-3 h-3 opacity-60" />
            </a>
          </div>

          <div className="rounded-2xl overflow-hidden border border-white/[0.06] bg-black/30" data-testid="spotify-embed-container">
            {embedUrl ? (
              <iframe key={embedUrl} src={embedUrl} width="100%" height="352" frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy" title={`Spotify - ${current?.name}`} style={{ borderRadius: '16px' }} />
            ) : (
              <div className="flex items-center justify-center h-[352px] text-white/30 text-sm">Selecciona un artista o playlist</div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── Floating Mini Player — bigger, mobile-friendly ─── */
export const SpotifyMiniPlayer = () => {
  const { artists, playlists } = useSpotifyData();
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [tab, setTab] = useState('artists');
  const [activeIdx, setActiveIdx] = useState(0);

  const items = tab === 'playlists' ? playlists : artists;

  useEffect(() => {
    if (expanded || dismissed) return;
    const iv = setInterval(() => {
      setActiveIdx(p => {
        const list = tab === 'playlists' ? playlists : artists;
        return list.length > 0 ? (p + 1) % list.length : 0;
      });
    }, 30000);
    return () => clearInterval(iv);
  }, [expanded, dismissed, artists, playlists, tab]);

  if (dismissed) return null;

  const current = items[activeIdx] || items[0];
  const embedUrl = spotifyEmbed(current?.spotify_url || '');

  return (
    <div className="fixed bottom-0 left-0 right-0 z-30" data-testid="spotify-mini-player"
      style={{ pointerEvents: 'none', paddingBottom: '8px' }}>
      <div className="max-w-3xl mx-auto px-3 sm:px-4" style={{ pointerEvents: 'auto' }}>
        <div className={`bg-[#0c0c0e] border border-[#1DB954]/20 shadow-2xl shadow-black/60 transition-all duration-300 overflow-hidden ${expanded ? 'rounded-2xl mb-2' : 'rounded-t-2xl'}`}>

          {/* ── Header bar — large touch target ── */}
          <div className="flex items-center justify-between px-4 sm:px-5 py-3 cursor-pointer select-none active:bg-white/[0.03]"
            onClick={() => setExpanded(!expanded)}>
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-11 h-11 rounded-full bg-[#1DB954] flex items-center justify-center shrink-0">
                <svg className="w-6 h-6 text-black" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-white truncate">OG MUSIC en Spotify</p>
                <p className="text-xs text-[#1DB954] truncate">{current?.name || 'Cargando...'}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-2">
              {/* Expand/collapse — big touch area */}
              <div className="w-11 h-11 rounded-full bg-[#1DB954]/15 border border-[#1DB954]/30 flex items-center justify-center" data-testid="mini-player-toggle">
                {expanded
                  ? <ChevronDown className="w-5 h-5 text-[#1DB954]" />
                  : <ChevronUp className="w-5 h-5 text-[#1DB954]" />
                }
              </div>
              <button onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/[0.06] text-white/40 active:bg-white/10"
                data-testid="mini-player-close">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* ── Expanded content ── */}
          {expanded && (
            <div className="px-4 sm:px-5 pb-4">
              {/* Artists / Playlists toggle */}
              <div className="flex gap-1 p-1 bg-white/[0.04] rounded-xl mb-3">
                <button onClick={() => { setTab('artists'); setActiveIdx(0); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all ${tab === 'artists' ? 'bg-[#1DB954] text-black' : 'text-white/40'}`}>
                  <User className="w-3.5 h-3.5" />Artistas
                </button>
                <button onClick={() => { setTab('playlists'); setActiveIdx(0); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all ${tab === 'playlists' ? 'bg-[#1DB954] text-black' : 'text-white/40'}`}>
                  <ListMusic className="w-3.5 h-3.5" />Playlists
                </button>
              </div>

              {/* Item selector */}
              <div className="flex gap-1.5 mb-3 overflow-x-auto pb-1 scrollbar-hide">
                {items.map((a, i) => (
                  <button key={a.id || i} onClick={() => setActiveIdx(i)}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-medium whitespace-nowrap transition-all ${
                      i === activeIdx ? 'bg-[#1DB954] text-black' : 'bg-white/[0.06] text-white/50'
                    }`} data-testid={`mini-item-${i}`}>{a.name}</button>
                ))}
              </div>

              {/* Spotify embed */}
              <div className="rounded-xl overflow-hidden">
                {embedUrl ? (
                  <iframe key={embedUrl} src={embedUrl} width="100%" height="352" frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                    loading="lazy" title={`Spotify - ${current?.name}`} style={{ borderRadius: '12px' }} />
                ) : (
                  <div className="flex items-center justify-center h-[200px] text-white/30 text-xs">Sin contenido</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
